// Fontsource: IA Writer fonts (import first so global CSS can use them)
import '@fontsource/ia-writer-quattro/400.css'
import '@fontsource/ia-writer-quattro/700.css'
import './assets/main.css'

import * as Sentry from '@sentry/vue'
import maplibregl from 'maplibre-gl'
import { createPinia } from 'pinia'
import { createApp, watch } from 'vue'

import App from './App.vue'
import { registerRoutecampE2EBridge } from './e2e/e2eBridge'
import { isE2eMode } from './e2e/e2eMode'
import router from './router'
import { installAndroidBackInterceptor } from './services/androidBackService'
import { connectivityService } from './services/connectivityService'
import { handleRoutecampDeepLinks } from './services/deepLinkService'
import { logService, LOG_CATEGORIES } from './services/logService'
import { maintenanceModeService } from './services/maintenanceModeService'
import { setupNativeOpenHandlers } from './services/nativeOpenService'
import { nativeTileProxyService } from './services/nativeTileProxyService'
import { setupNativeTrackFileImport } from './services/nativeTrackFileImportService'
import { registerOfflineProtocol } from './services/offlineMapProtocol'
import { onlineDataRefreshService } from './services/onlineDataRefreshService'
import { isTauri } from './services/platformService'
import { ensureRoutecampContourProtocols } from './services/routecampContours'
import { requestRouteModeLeave } from './services/routeModeLeaveGuardService'
import { syncService } from './services/syncService'
import { setupTauriCloseGuard } from './services/tauriWindowService'
import { uiSettingsSyncService } from './services/uiSettingsSyncService'
import { uiStatePersistenceService } from './services/uiStatePersistenceService'
import { useAuthStore } from './stores/authStore'
import { useEventStore } from './stores/eventStore'
import { useOfflineStore } from './stores/offlineStore'
import { usePhotoStore } from './stores/photoStore'
import { useUiStore } from './stores/uiStore'
import { useWorkspaceStore } from './stores/workspaceStore'

ensureRoutecampContourProtocols(maplibregl)

if (isTauri()) {
  registerOfflineProtocol()
}

// Create pinia instance
const app = createApp(App)
const pinia = createPinia()

const sentryTunnel = import.meta.env.VITE_SENTRY_TUNNEL?.trim()

// Only initialize Sentry in production
if (import.meta.env.PROD) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    ...(sentryTunnel ? { tunnel: sentryTunnel } : {}),
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
  })
}

app.use(pinia)
app.use(router)

router.beforeEach(async () => {
  return await requestRouteModeLeave()
})

router.afterEach((to) => {
  const normalizeParam = (value: unknown): string | null => {
    if (typeof value === 'string') return value
    if (Array.isArray(value)) return value[0] ?? null
    return null
  }

  uiStatePersistenceService.updateRoute({
    fullPath: to.fullPath,
    workspaceId: normalizeParam(to.params.workspaceId),
    photoId: normalizeParam(to.params.photoId),
    tourId: normalizeParam(to.params.tourId),
  })
})

// On Tauri Android, let open overlays consume the hardware back button before
// it pops the route (native handler already does history-back / app-exit).
installAndroidBackInterceptor(router)

// Function to initialize all stores with data from IndexedDB
const initializeStores = async () => {
  const authStore = useAuthStore()
  const eventStore = useEventStore()
  const workspaceStore = useWorkspaceStore()
  const photoStore = usePhotoStore()
  const uiStore = useUiStore()

  const offlineStore = useOfflineStore()
  let nativePendingResumeReady = false

  // Rust starts the native tile proxy in cache-only mode. Apply effective connectivity before any
  // map can mount so user-forced offline mode is also a native network boundary.
  await nativeTileProxyService.initialize()

  // Native offline storage must know its authenticated owner before any package reader initializes.
  await authStore.init()

  // Initialize remaining stores
  await Promise.all([eventStore.init(), workspaceStore.init(), photoStore.init(), uiStore.init()])

  if (isTauri()) {
    watch(connectivityService.isEffectivelyOnline, (online, wasOnline) => {
      if (!nativePendingResumeReady || !online || wasOnline !== false) return

      offlineStore.resumePendingDownloads().catch((error) => {
        logService.error(
          LOG_CATEGORIES.OFFLINE,
          'Failed to resume offline downloads on reconnect',
          {
            error,
          },
        )
      })
    })

    document.addEventListener('visibilitychange', () => {
      if (!nativePendingResumeReady || document.visibilityState !== 'visible') return

      offlineStore.resumePendingDownloads().catch((error) => {
        logService.error(
          LOG_CATEGORIES.OFFLINE,
          'Failed to resume offline downloads on visibility change',
          {
            error,
          },
        )
      })
    })
  }

  if (isE2eMode()) {
    registerRoutecampE2EBridge()
  }

  const restoredSnapshot = uiStatePersistenceService.getSnapshot()

  if (restoredSnapshot.ui) {
    const {
      isSidebarVisible,
      isLayersPanelVisible,
      activePanel,
      isMobileBottomPanelExpanded,
      isInWorkspacesOverview,
      autoViewportAdaptationEnabled,
    } = restoredSnapshot.ui

    if (typeof isSidebarVisible === 'boolean') {
      if (isSidebarVisible) {
        uiStore.showSidebar()
      } else {
        uiStore.hideSidebar()
      }
    }

    if (typeof isLayersPanelVisible === 'boolean') {
      if (isLayersPanelVisible) {
        uiStore.showLayersPanel()
      } else {
        uiStore.hideLayersPanel()
      }
    }

    if (typeof isMobileBottomPanelExpanded === 'boolean') {
      if (isMobileBottomPanelExpanded) {
        uiStore.expandMobileBottomPanel()
      } else {
        uiStore.collapseMobileBottomPanel()
      }
    }

    if (typeof isInWorkspacesOverview === 'boolean') {
      uiStore.setWorkspacesOverview(isInWorkspacesOverview)
    }

    if (typeof autoViewportAdaptationEnabled === 'boolean') {
      uiStore.setAutoViewportAdaptationEnabled(autoViewportAdaptationEnabled)
    }

    if (activePanel === 'tourDetail') {
      uiStore.showPanel(activePanel)
    } else if (activePanel === null) {
      uiStore.hidePanel()
    }
  }

  // Restore last visited route only if user launches into root route
  if (restoredSnapshot.route?.fullPath) {
    const currentFullPath = router.currentRoute.value.fullPath
    const targetFullPath = restoredSnapshot.route.fullPath
    if (currentFullPath === '/' && targetFullPath !== '/' && targetFullPath !== currentFullPath) {
      try {
        await router.replace(targetFullPath)
      } catch (error) {
        logService.error(LOG_CATEGORIES.GENERAL, 'Failed to restore last route', {
          error,
          targetFullPath,
        })
      }
    }
  }

  if (isTauri()) {
    const { invoke } = await import('@tauri-apps/api/core')
    const { listen } = await import('@tauri-apps/api/event')
    const { getCurrent, onOpenUrl } = await import('@tauri-apps/plugin-deep-link')
    const drainNativeRoutecampDeepLinks = async () => {
      try {
        const urls = await invoke<string[]>('take_deep_links')
        if (urls.length) {
          await handleRoutecampDeepLinks(router, urls)
        }
      } catch (error) {
        logService.error(LOG_CATEGORIES.GENERAL, 'Failed to read native deep links', { error })
      }
    }

    await listen('routecamp-deep-links', () => {
      void drainNativeRoutecampDeepLinks()
    })

    const currentUrls = await getCurrent()
    if (currentUrls?.length) {
      await handleRoutecampDeepLinks(router, currentUrls)
    }

    await drainNativeRoutecampDeepLinks()

    await onOpenUrl((urls) => {
      void handleRoutecampDeepLinks(router, urls)
    })

    window.addEventListener('focus', () => {
      void drainNativeRoutecampDeepLinks()
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return
      void drainNativeRoutecampDeepLinks()
    })
  }

  // Setup automatic sync after stores are initialized
  uiSettingsSyncService.setup()
  syncService.setupAutomaticSync()
  onlineDataRefreshService.setup()

  // Ask the browser/webview to protect our origin storage from eviction — the sync queue
  // (including queued photo files) lives in IndexedDB and must survive long offline periods.
  if (navigator.storage?.persist) {
    navigator.storage
      .persist()
      .then((persisted) => {
        logService.info(LOG_CATEGORIES.DATABASE, 'Storage persistence requested', { persisted })
      })
      .catch((error) => {
        logService.warn(LOG_CATEGORIES.DATABASE, 'Storage persistence request failed', { error })
      })
  }

  // Guard native desktop window close while unsynced changes exist (§9); no-op on web/mobile.
  void setupTauriCloseGuard(() => eventStore.unsyncedEvents.length)

  // Set up viewport height handling for iOS
  const setVh = () => {
    // Use the LAYOUT viewport (innerHeight), not visualViewport: visualViewport.height shrinks
    // when the on-screen keyboard opens and can stick at the reduced value on a standalone iOS
    // PWA, which would shrink the whole app (the map then leaves a white gap). Keyboard-aware
    // surfaces (e.g. the search results sheet) read visualViewport themselves.
    const viewportHeight = window.innerHeight
    const vh = viewportHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
  }

  // Set initial viewport height
  setVh()

  // Update on resize and orientation change
  window.addEventListener('resize', setVh)
  window.visualViewport?.addEventListener('resize', setVh)
  window.addEventListener('orientationchange', () => {
    // Delay for iOS to complete orientation change
    setTimeout(setVh, 100)
  })

  await router.isReady()

  // If the persisted snapshot had the mobile panel expanded but we're now on a route
  // that has no workspace detail (e.g. the collections overview), collapse it before
  // the app mounts so the panel never renders in the wrong expanded state.
  // The route is guaranteed to be settled here (after router.isReady()).
  const isWorkspaceDetailRoute = !!router.currentRoute.value.params.workspaceId
  if (!isWorkspaceDetailRoute && uiStore.isMobileBottomPanelExpanded) {
    uiStore.collapseMobileBottomPanel()
  }

  // Mount the app after initialization
  app.mount('#app')
  if (isTauri()) {
    // Hydrate and resume large pending plans only after the browser has had a
    // chance to paint the mounted application.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void offlineStore.init().then(() => {
          nativePendingResumeReady = true
          return offlineStore.resumePendingDownloads()
        })
      })
    })
  }
  maintenanceModeService.setup()

  setupNativeTrackFileImport(router)

  if (isTauri()) {
    setupNativeOpenHandlers().catch((error) => {
      logService.error(LOG_CATEGORIES.FILE, 'Failed to set up native file open handling', {
        error,
      })
    })
  }
}

// Initialize stores and mount app
initializeStores().catch((error) => {
  logService.error(
    LOG_CATEGORIES.GENERAL,
    `Failed to initialize stores: ${error instanceof Error ? error.message : String(error)}`,
    { error },
  )
  // Set up viewport height handling for iOS even if init fails
  const setVh = () => {
    // Use the LAYOUT viewport (innerHeight), not visualViewport: visualViewport.height shrinks
    // when the on-screen keyboard opens and can stick at the reduced value on a standalone iOS
    // PWA, which would shrink the whole app (the map then leaves a white gap). Keyboard-aware
    // surfaces (e.g. the search results sheet) read visualViewport themselves.
    const viewportHeight = window.innerHeight
    const vh = viewportHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
  }

  setVh()
  window.addEventListener('resize', setVh)
  window.visualViewport?.addEventListener('resize', setVh)
  window.addEventListener('orientationchange', () => {
    setTimeout(setVh, 100)
  })

  // Mount the app even if initialization fails
  app.mount('#app')
  maintenanceModeService.setup()
})
